import {
    argbFromHex,
    hexFromArgb,
    Hct,
    SchemeTonalSpot,
    MaterialDynamicColors,
    sourceColorFromImage
} from '@material/material-color-utilities';
import { syncNativeSystemBars } from './nativeShell.js';

export const DEFAULT_SEED_COLOR = '#E50914'; // Netflix Red

// Mappatura token camelCase -> kebab-case
const COLOR_ROLES = [
    { prop: 'primary', css: '--md-sys-color-primary' },
    { prop: 'onPrimary', css: '--md-sys-color-on-primary' },
    { prop: 'primaryContainer', css: '--md-sys-color-primary-container' },
    { prop: 'onPrimaryContainer', css: '--md-sys-color-on-primary-container' },
    { prop: 'inversePrimary', css: '--md-sys-color-inverse-primary' },

    { prop: 'secondary', css: '--md-sys-color-secondary' },
    { prop: 'onSecondary', css: '--md-sys-color-on-secondary' },
    { prop: 'secondaryContainer', css: '--md-sys-color-secondary-container' },
    { prop: 'onSecondaryContainer', css: '--md-sys-color-on-secondary-container' },

    { prop: 'tertiary', css: '--md-sys-color-tertiary' },
    { prop: 'onTertiary', css: '--md-sys-color-on-tertiary' },
    { prop: 'tertiaryContainer', css: '--md-sys-color-tertiary-container' },
    { prop: 'onTertiaryContainer', css: '--md-sys-color-on-tertiary-container' },

    { prop: 'error', css: '--md-sys-color-error' },
    { prop: 'onError', css: '--md-sys-color-on-error' },
    { prop: 'errorContainer', css: '--md-sys-color-error-container' },
    { prop: 'onErrorContainer', css: '--md-sys-color-on-error-container' },

    { prop: 'background', css: '--md-sys-color-background' },
    { prop: 'onBackground', css: '--md-sys-color-on-background' },

    { prop: 'surface', css: '--md-sys-color-surface' },
    { prop: 'onSurface', css: '--md-sys-color-on-surface' },
    { prop: 'surfaceDim', css: '--md-sys-color-surface-dim' },
    { prop: 'surfaceBright', css: '--md-sys-color-surface-bright' },
    { prop: 'surfaceContainerLowest', css: '--md-sys-color-surface-container-lowest' },
    { prop: 'surfaceContainerLow', css: '--md-sys-color-surface-container-low' },
    { prop: 'surfaceContainer', css: '--md-sys-color-surface-container' },
    { prop: 'surfaceContainerHigh', css: '--md-sys-color-surface-container-high' },
    { prop: 'surfaceContainerHighest', css: '--md-sys-color-surface-container-highest' },
    { prop: 'surfaceVariant', css: '--md-sys-color-surface-variant' },
    { prop: 'onSurfaceVariant', css: '--md-sys-color-on-surface-variant' },
    { prop: 'surfaceTint', css: '--md-sys-color-surface-tint' },

    { prop: 'inverseSurface', css: '--md-sys-color-inverse-surface' },
    { prop: 'inverseOnSurface', css: '--md-sys-color-inverse-on-surface' },

    { prop: 'outline', css: '--md-sys-color-outline' },
    { prop: 'outlineVariant', css: '--md-sys-color-outline-variant' },
    { prop: 'scrim', css: '--md-sys-color-scrim' },
    { prop: 'shadow', css: '--md-sys-color-shadow' },
];

const colorCache = new Map();

/**
 * Converte il livello di contrasto stringa in float per Material Dynamic Colors
 * standard: 0.0, medium: 0.5, high: 1.0
 */
export function getContrastValue(level) {
    if (level === 'high') return 1.0;
    if (level === 'medium') return 0.5;
    return 0.0;
}

/**
 * Estrae il colore seed dominante da un'immagine (Content extraction / Dynamic color)
 */
export async function extractColorFromImage(imageUrl) {
    if (!imageUrl) return null;
    if (colorCache.has(imageUrl)) return colorCache.get(imageUrl);

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.referrerPolicy = 'no-referrer';

        const timeout = setTimeout(() => {
            resolve(null);
        }, 3000);

        img.onload = () => {
            clearTimeout(timeout);
            try {
                const argb = sourceColorFromImage(img);
                const hex = hexFromArgb(argb);
                colorCache.set(imageUrl, hex);
                resolve(hex);
            } catch (err) {
                console.warn('[themeService] Impossibile estrarre colore dall\'immagine:', err);
                resolve(null);
            }
        };

        img.onerror = () => {
            clearTimeout(timeout);
            resolve(null);
        };

        img.src = imageUrl;
    });
}

/**
 * Applica i token M3 al documento HTML tramite CSS Custom Properties
 */
export function applyTheme({
    seedColor = DEFAULT_SEED_COLOR,
    isDark = true,
    contrastLevel = 'standard'
} = {}) {
    try {
        const contrastVal = getContrastValue(contrastLevel);
        const hct = Hct.fromInt(argbFromHex(seedColor));
        const scheme = new SchemeTonalSpot(hct, isDark, contrastVal);

        const root = document.documentElement;
        root.dataset.theme = isDark ? 'dark' : 'light';
        root.style.colorScheme = isDark ? 'dark' : 'light';

        COLOR_ROLES.forEach(({ prop, css }) => {
            const dynamicColor = MaterialDynamicColors[prop];
            if (dynamicColor) {
                const argb = dynamicColor.getArgb(scheme);
                const hex = hexFromArgb(argb);
                root.style.setProperty(css, hex);
            }
        });

        // La barra di sistema su mobile segue la superficie del tema
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'theme-color';
            document.head.appendChild(meta);
        }
        meta.content = hexFromArgb(MaterialDynamicColors.surface.getArgb(scheme));
        syncNativeSystemBars(isDark);

        return true;
    } catch (err) {
        console.error('[themeService] Errore durante l\'applicazione del tema:', err);
        return false;
    }
}

function readPrefs() {
    const read = (key) => {
        try { return localStorage.getItem(key); } catch { return null; }
    };
    // m3_is_dark e' la vecchia chiave booleana: migrata a m3_theme_mode
    const legacyIsDark = read('m3_is_dark');
    let themeMode = read('m3_theme_mode');
    if (!['dark', 'light', 'system'].includes(themeMode)) {
        themeMode = legacyIsDark === null ? 'dark' : (legacyIsDark === 'true' ? 'dark' : 'light'); // Default dark per app di streaming
    }
    let contrastLevel = read('m3_contrast');
    if (!contrastLevel) {
        contrastLevel = window.matchMedia?.('(prefers-contrast: more)').matches ? 'high' : 'standard';
    }
    return {
        seedColor: read('m3_seed_color') || DEFAULT_SEED_COLOR,
        themeMode,
        contrastLevel,
        // chi ha scelto un colore ha deciso quale vuole vedere: il colore dalla copertina
        // resta attivo di default solo se non e' mai stato scelto un seed
        dynamicContent: read('m3_dynamic_content') !== null
            ? read('m3_dynamic_content') !== 'false'
            : read('m3_seed_color') === null
    };
}

const PREF_KEYS = {
    seedColor: 'm3_seed_color',
    themeMode: 'm3_theme_mode',
    contrastLevel: 'm3_contrast',
    dynamicContent: 'm3_dynamic_content'
};

// Store esterno: le preferenze dell'utente e il seed temporaneo del contenuto
// restano separati, cosi' un seed estratto da una locandina non finisce mai
// nelle preferenze ne' viene riapplicato da un cambio di contrasto.
let prefs = null;
let contentSeed = null;
let extractToken = 0;
let snapshot = null;
const listeners = new Set();

const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)');

function resolveIsDark() {
    return prefs.themeMode === 'system' ? (systemDark?.matches ?? true) : prefs.themeMode === 'dark';
}

function render() {
    const isDark = resolveIsDark();
    applyTheme({
        seedColor: prefs.dynamicContent && contentSeed ? contentSeed : prefs.seedColor,
        isDark,
        contrastLevel: prefs.contrastLevel
    });
    snapshot = { ...prefs, isDark };
    listeners.forEach((l) => l());
}

/**
 * Inizializza il tema con le preferenze memorizzate. Idempotente.
 */
export function initTheme() {
    if (!prefs) {
        prefs = readPrefs();
        render();
        systemDark?.addEventListener('change', () => {
            if (prefs.themeMode === 'system') render();
        });
    }
    return snapshot;
}

export function subscribeTheme(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getThemeSnapshot() {
    return initTheme();
}

export function setThemePrefs({ isDark, ...partial }) {
    initTheme();
    if (isDark !== undefined) partial.themeMode = isDark ? 'dark' : 'light';
    // scelta esplicita di un colore: la copertina non deve piu' sovrascriverlo
    if (partial.seedColor !== undefined && partial.dynamicContent === undefined) partial.dynamicContent = false;
    prefs = { ...prefs, ...partial };
    Object.keys(partial).forEach((k) => {
        try { localStorage.setItem(PREF_KEYS[k], String(partial[k])); } catch { /* storage non disponibile */ }
    });
    render();
}

/**
 * Colora il tema con la locandina del contenuto in vista (non persistito).
 * Una risposta in ritardo non sovrascrive una richiesta piu' recente.
 */
export async function applyContentSeed(imageUrl) {
    initTheme();
    if (!prefs.dynamicContent) return;
    const token = ++extractToken;
    const hex = await extractColorFromImage(imageUrl);
    if (token !== extractToken || !hex || hex === contentSeed) return;
    contentSeed = hex;
    render();
}

/** Torna al seed scelto dall'utente. */
export function clearContentSeed() {
    extractToken++;
    if (contentSeed === null) return;
    contentSeed = null;
    if (prefs) render();
}
